/**
 * Cart Combination Synchronizer
 * Automatically renumbers combination letter assignments when items are removed from cart
 * Ensures combinations stay sequential: A, B, C, D... even after deletions
 * @version 1.0.0
 */

class CartCombinationSync {
    constructor() {
        this.isProcessing = false;
        this.syncPromise = null;
        this.init();
    }

    init() {
        // Listen for cart changes
        document.documentElement.addEventListener('cart:change', (e) => {
            this.handleCartChange(e);
        });

        // Also listen for direct cart updates
        document.addEventListener('cart:updated', (e) => {
            this.handleCartChange(e);
        });

        // Intercept checkout clicks to ensure sync completes first
        this.interceptCheckoutClicks();

        // If we're on the cart page, run sync immediately on load and intercept cart actions
        if (this.isCartPage()) {
            this.handleCartPageSync();
            this.interceptCartPageActions();
        }
    }

    isCartPage() {
        // Check if we're on the /cart page (not cart drawer)
        return window.location.pathname === '/cart' ||
            window.location.pathname.startsWith('/cart/') ||
            document.body.classList.contains('template-cart');
    }

    async handleCartPageSync() {
        // Run sync immediately on cart page load
        const shouldSync = await this.shouldSyncCart();
        if (shouldSync) {
            console.log('🛒 Cart page loaded - running sync check...');
            const changesMade = await this.handleCartChange({ source: 'page-load' });

            // If sync made changes, reload the page to show updated state
            if (changesMade) {
                console.log('🔄 Sync made changes - reloading page...');
                window.location.reload();
            }
        }
    }

    interceptCartPageActions() {
        // Intercept cart form submissions and quantity changes on cart page
        document.addEventListener('submit', async (e) => {
            const form = e.target;

            // Check if it's a cart form
            if (form.matches('form[action*="/cart"]') || form.closest('.cart-form, [data-cart-form]')) {
                if (this.isProcessing) {
                    e.preventDefault();
                    console.log('⏳ Cart sync in progress - waiting before form submission...');

                    // Wait for sync to complete
                    if (this.syncPromise) {
                        await this.syncPromise;
                    }

                    console.log('✅ Sync complete - submitting form');
                    // Re-submit the form
                    form.submit();
                }
            }
        });

        // Intercept quantity changes and remove buttons
        document.addEventListener('click', async (e) => {
            const target = e.target.closest('[name="update"], [data-cart-remove], .cart-remove, [href*="/cart/change"]');

            if (target && this.isProcessing) {
                e.preventDefault();
                e.stopPropagation();

                console.log('⏳ Cart sync in progress - waiting before cart action...');

                // Wait for sync to complete
                if (this.syncPromise) {
                    await this.syncPromise;
                }

                console.log('✅ Sync complete - proceeding with action');

                // Proceed with the original action
                if (target.tagName === 'A') {
                    window.location.href = target.href;
                } else if (target.type === 'submit') {
                    target.form?.submit();
                } else {
                    target.click();
                }
            }
        }, true);
    }

    interceptCheckoutClicks() {
        // Use event delegation to catch checkout button clicks
        document.addEventListener('click', async (e) => {
            // Check if click is on a checkout button/link
            const target = e.target.closest('a[href*="/checkout"], button[name="checkout"], [data-checkout-button]');

            if (target && this.isProcessing) {
                // Sync is in progress - prevent navigation and wait
                e.preventDefault();
                e.stopPropagation();

                // Show a brief loading state on the button if possible
                const originalText = target.textContent;
                const isButton = target.tagName === 'BUTTON';
                if (isButton) {
                    target.disabled = true;
                    target.textContent = 'Syncing...';
                }

                // Wait for sync to complete
                if (this.syncPromise) {
                    await this.syncPromise;
                }

                // Restore button state
                if (isButton) {
                    target.disabled = false;
                    target.textContent = originalText;
                }

                // Now proceed with checkout
                if (target.tagName === 'A') {
                    window.location.href = target.href;
                } else {
                    target.click();
                }
            }
        }, true); // Use capture phase to catch before other handlers
    }

    async handleCartChange(event) {
        // Prevent concurrent processing
        if (this.isProcessing) {
            return false;
        }

        this.isProcessing = true;
        let changesMade = false;

        // Show cart loading state
        this.showCartLoading(true);

        // Create a promise that tracks the sync operation
        this.syncPromise = (async () => {
            try {
                // Check FIRST if there are any customization properties - exit immediately if not
                const shouldSync = await this.shouldSyncCart();

                if (!shouldSync) {
                    // No customization properties in cart - exit immediately without any delays
                    return false;
                }

                // Small delay to let cart settle (only if we have customizations to sync)
                await new Promise(resolve => setTimeout(resolve, 300));

                // Check for orphaned accessories (main product removed)
                const removedAccessories = await this.removeOrphanedAccessories();
                if (removedAccessories) changesMade = true;

                // Check for standalone main products (all accessories removed)
                const removedCustomizations = await this.removeCustomizationFromStandaloneProducts();
                if (removedCustomizations) changesMade = true;

                // Then sync combination letters
                const renamedCombinations = await this.syncCombinations();
                if (renamedCombinations) changesMade = true;

                // If any changes were made, update cart count bubble
                if (changesMade) {
                    await this.updateCartCount();
                }

                return changesMade;
            } catch (error) {
                console.error('Error syncing combinations:', error);
                return false;
            }
        })();

        // Wait for sync to complete before resetting flag
        changesMade = await this.syncPromise;
        this.isProcessing = false;
        this.syncPromise = null;

        // Hide cart loading state
        this.showCartLoading(false);

        return changesMade;
    }

    showCartLoading(isLoading) {
        try {
            // Target cart drawer element
            const cartDrawer = document.querySelector('cart-drawer, #cart-drawer, [data-cart-drawer]');
            
            if (cartDrawer) {
                if (isLoading) {
                    // Add busy/loading state - theme uses 'busy' attribute
                    cartDrawer.setAttribute('busy', '');
                    cartDrawer.classList.add('is-loading');
                    console.log('🔄 Cart sync in progress...');
                } else {
                    // Remove busy/loading state
                    cartDrawer.removeAttribute('busy');
                    cartDrawer.classList.remove('is-loading');
                    console.log('✅ Cart sync complete');
                }
            }

            // Also target the main cart page if we're on it
            if (this.isCartPage()) {
                const cartMain = document.querySelector('.cart, [data-cart], main.cart');
                if (cartMain) {
                    if (isLoading) {
                        cartMain.classList.add('is-loading');
                    } else {
                        cartMain.classList.remove('is-loading');
                    }
                }
            }
        } catch (error) {
            console.error('Error toggling cart loading state:', error);
        }
    }

    async updateCartCount() {
        try {
            // Fetch the latest cart state
            const cartResponse = await fetch(`${Shopify.routes.root}cart.js`);
            const cart = await cartResponse.json();

            // Dispatch cart:change event to update all cart-related UI including bubble count
            document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
                bubbles: true,
                detail: {
                    cart: cart
                }
            }));

            // Also update any cart count elements directly (fallback)
            const cartCount = cart.item_count;
            document.querySelectorAll('[data-cart-count], .cart-count, #cart-icon-bubble').forEach(el => {
                el.textContent = cartCount;

                // Toggle visibility for bubble elements
                if (cartCount > 0) {
                    el.classList.remove('hidden');
                    el.removeAttribute('hidden');
                } else {
                    el.classList.add('hidden');
                    el.setAttribute('hidden', '');
                }
            });

            console.log(`✅ Updated cart count to ${cartCount}`);
        } catch (error) {
            console.error('Error updating cart count:', error);
        }
    }

    async shouldSyncCart() {
        try {
            const cartResponse = await fetch(`${Shopify.routes.root}cart.js`);
            const cart = await cartResponse.json();

            // Only sync if cart has items with Customization properties
            if (!cart.items || cart.items.length === 0) {
                return false;
            }

            return cart.items.some(item =>
                item.properties && item.properties.Customization
            );
        } catch (error) {
            console.error('Error checking cart:', error);
            return false;
        }
    }

    async removeOrphanedAccessories() {
        try {
            // Fetch current cart state
            const cartResponse = await fetch(`${Shopify.routes.root}cart.js`);
            const cart = await cartResponse.json();

            if (!cart.items || cart.items.length === 0) {
                return false; // Empty cart, nothing to check
            }

            // Group items by customization ID
            const customizationGroups = new Map();

            cart.items.forEach((item, index) => {
                if (item.properties && item.properties.Customization) {
                    const combo = item.properties.Customization;
                    const role = item.properties._customization_role || 'unknown';

                    if (!customizationGroups.has(combo)) {
                        customizationGroups.set(combo, {
                            main: null,
                            accessories: []
                        });
                    }

                    const group = customizationGroups.get(combo);

                    if (role === 'main') {
                        group.main = { item, cartIndex: index + 1 };
                    } else if (role === 'accessory') {
                        group.accessories.push({ item, cartIndex: index + 1 });
                    }
                }
            });

            // Find accessories without main products (orphaned)
            const orphanedAccessories = [];

            customizationGroups.forEach((group, combo) => {
                // If no main product but has accessories, they're orphaned
                if (!group.main && group.accessories.length > 0) {
                    group.accessories.forEach(acc => {
                        orphanedAccessories.push(acc.cartIndex);
                    });
                }
            });

            // Remove orphaned accessories
            if (orphanedAccessories.length > 0) {
                // CRITICAL: Sort indices in descending order to remove from bottom up
                // This prevents index shifting issues when removing multiple items
                orphanedAccessories.sort((a, b) => b - a);

                for (const lineIndex of orphanedAccessories) {
                    try {
                        await fetch(`${Shopify.routes.root}cart/change.js`, {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json',
                                'X-Requested-With': 'XMLHttpRequest'
                            },
                            body: JSON.stringify({
                                line: lineIndex,
                                quantity: 0  // Set to 0 to remove
                            })
                        });

                        // Small delay between removals
                        await new Promise(resolve => setTimeout(resolve, 100));
                    } catch (error) {
                        console.error('Remove accessory failed:', error.message);
                    }
                }

                // Trigger a cart refresh to update UI
                document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
                    bubbles: true
                }));

                return true; // Changes were made
            }

            return false; // No changes

        } catch (error) {
            console.error('Error removing orphaned accessories:', error);
            return false;
        }
    }

    async removeCustomizationFromStandaloneProducts() {
        try {
            // Fetch current cart state
            const cartResponse = await fetch(`${Shopify.routes.root}cart.js`);
            const cart = await cartResponse.json();

            if (!cart.items || cart.items.length === 0) {
                return false; // Empty cart, nothing to check
            }

            // Group items by customization ID
            const customizationGroups = new Map();

            cart.items.forEach((item, index) => {
                if (item.properties && item.properties.Customization) {
                    const combo = item.properties.Customization;
                    const role = item.properties._customization_role || 'unknown';

                    if (!customizationGroups.has(combo)) {
                        customizationGroups.set(combo, {
                            main: null,
                            accessories: []
                        });
                    }

                    const group = customizationGroups.get(combo);

                    if (role === 'main') {
                        group.main = { item, cartIndex: index + 1 };
                    } else if (role === 'accessory') {
                        group.accessories.push({ item, cartIndex: index + 1 });
                    }
                }
            });

            // Find main products without accessories (standalone)
            const standaloneMainProducts = [];

            customizationGroups.forEach((group, combo) => {
                // If main product exists but has NO accessories, it's standalone
                if (group.main && group.accessories.length === 0) {
                    standaloneMainProducts.push({
                        cartIndex: group.main.cartIndex,
                        item: group.main.item,
                        combo: combo
                    });
                }
            });

            // Remove customization properties from standalone main products
            if (standaloneMainProducts.length > 0) {
                for (const { cartIndex, item } of standaloneMainProducts) {
                    try {
                        // Create new properties object and explicitly set Customization fields to empty string to remove them
                        const newProperties = {};
                        Object.keys(item.properties).forEach(key => {
                            if (key !== 'Customization' && key !== '_customization_role') {
                                newProperties[key] = item.properties[key];
                            }
                        });

                        // Explicitly set customization properties to empty string to remove them
                        newProperties['Customization'] = '';
                        newProperties['_customization_role'] = '';

                        await fetch(`${Shopify.routes.root}cart/change.js`, {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json',
                                'X-Requested-With': 'XMLHttpRequest'
                            },
                            body: JSON.stringify({
                                line: cartIndex,
                                properties: newProperties
                            })
                        });

                        // Small delay between updates
                        await new Promise(resolve => setTimeout(resolve, 100));
                    } catch (error) {
                        console.error('Remove customization failed:', error.message);
                    }
                }

                // Trigger a cart refresh to update UI
                document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
                    bubbles: true
                }));

                return true; // Changes were made
            }

            return false; // No changes

        } catch (error) {
            console.error('Error removing customization from standalone products:', error);
            return false;
        }
    }

    async syncCombinations() {
        try {
            // Fetch current cart state
            const cartResponse = await fetch(`${Shopify.routes.root}cart.js`);
            const cart = await cartResponse.json();

            if (!cart.items || cart.items.length === 0) {
                return false; // Empty cart, nothing to sync
            }

            // Group items by their current combination
            const combinationGroups = new Map();
            const itemsWithoutCombination = [];

            cart.items.forEach((item, index) => {
                if (item.properties && item.properties.Customization) {
                    const combo = item.properties.Customization;
                    if (!combinationGroups.has(combo)) {
                        combinationGroups.set(combo, []);
                    }
                    combinationGroups.get(combo).push({
                        item,
                        cartIndex: index + 1 // Cart API uses 1-based indexing
                    });
                } else {
                    itemsWithoutCombination.push({
                        item,
                        cartIndex: index + 1
                    });
                }
            });

            // If no combinations exist, nothing to sync
            if (combinationGroups.size === 0) {
                return false;
            }

            // Get sorted list of current combinations
            const existingCombinations = Array.from(combinationGroups.keys())
                .map(combo => ({
                    combo,
                    number: this.combinationToNumber(combo)
                }))
                .sort((a, b) => a.number - b.number);

            // Check if renumbering is needed
            let needsRenumbering = false;
            for (let i = 0; i < existingCombinations.length; i++) {
                const expectedCombo = this.numberToCombination(i);
                if (existingCombinations[i].combo !== expectedCombo) {
                    needsRenumbering = true;
                    break;
                }
            }

            // If combinations are already sequential (A, B, C...), no action needed
            if (!needsRenumbering) {
                return false;
            }

            // Renumber combinations sequentially
            const updates = [];
            existingCombinations.forEach((existing, index) => {
                const newCombo = this.numberToCombination(index);

                if (existing.combo !== newCombo) {
                    // Need to update this group
                    const itemsInGroup = combinationGroups.get(existing.combo);

                    itemsInGroup.forEach(({ item, cartIndex }) => {
                        updates.push({
                            line: cartIndex,
                            properties: {
                                ...item.properties,
                                'Customization': newCombo
                            }
                        });
                    });
                }
            });

            // Execute updates if needed
            if (updates.length > 0) {
                await this.updateCartProperties(updates);

                // Trigger a cart refresh to update UI
                document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
                    bubbles: true
                }));

                return true; // Changes were made
            }

            return false; // No changes

        } catch (error) {
            console.error('Error in syncCombinations:', error);
            return false;
        }
    }

    async updateCartProperties(updates) {
        // Use Shopify's cart/change.js endpoint to update properties
        // We need to update items one by one
        for (const update of updates) {
            try {
                await fetch(`${Shopify.routes.root}cart/change.js`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-Requested-With': 'XMLHttpRequest'
                    },
                    body: JSON.stringify({
                        line: update.line,
                        properties: update.properties
                    })
                });

                // Small delay between updates to avoid rate limiting
                await new Promise(resolve => setTimeout(resolve, 100));
            } catch (error) {
                console.error('Cart update failed:', error.message);
            }
        }
    }

    numberToCombination(counter) {
        // Generate combination ID: A, B, C, ... Z, AA, AB, ... ZZ, AAA, etc.
        let id = '';
        let num = counter;

        do {
            id = String.fromCharCode(65 + (num % 26)) + id;
            num = Math.floor(num / 26) - 1;
        } while (num >= 0);

        return id;
    }

    combinationToNumber(combination) {
        // Convert combination string (A, B, ..., Z, AA, AB) back to number
        let num = 0;
        for (let i = 0; i < combination.length; i++) {
            num = num * 26 + (combination.charCodeAt(i) - 65 + 1);
        }
        return num - 1;
    }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
        new CartCombinationSync();
    });
} else {
    new CartCombinationSync();
}
